"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, memo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
interface Logo {
  src: string;
  alt: string;
}
interface MorphingLogoProps {
  logos: Logo[];
  initialDelay?: number;
  morphInterval?: number;
  className?: string;
  imageClassName?: string;
  width?: number;
  height?: number;
}

// Stable transition configs
const morphTransition = {
  duration: 0.4,
  ease: [0.4, 0, 0.2, 1]
} as const;
const hoverTransition = {
  duration: 0.5,
  ease: [0.16, 1, 0.3, 1] // Slow, luxurious ease-out
} as const;
function MorphingLogoInner(t0) {
  const $ = _c(38);
  const {
    logos,
    initialDelay: t1,
    morphInterval: t2,
    className,
    imageClassName,
    width,
    height
  } = t0;
  const initialDelay = t1 === undefined ? 0 : t1;
  const morphInterval = t2 === undefined ? 4000 : t2;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const containerRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (!containerRef.current) {
        return;
      }
      const observer = new IntersectionObserver(t5 => {
        const [entry] = t5;
        return setIsVisible(entry.isIntersecting);
      }, {
        threshold: 0.1
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[2] !== initialDelay || $[3] !== isVisible || $[4] !== logos.length || $[5] !== morphInterval || $[6] !== shouldReduceMotion) {
    t5 = () => {
      if (logos.length <= 1 || shouldReduceMotion || !isVisible) {
        return;
      }
      let intervalId;
      const timeoutId = setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % logos.length);
        intervalId = setInterval(() => {
          setCurrentIndex(prev_0 => (prev_0 + 1) % logos.length);
        }, morphInterval);
      }, initialDelay);
      return () => {
        clearTimeout(timeoutId);
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    };
    t6 = [shouldReduceMotion, isVisible, initialDelay, morphInterval, logos.length];
    $[2] = initialDelay;
    $[3] = isVisible;
    $[4] = logos.length;
    $[5] = morphInterval;
    $[6] = shouldReduceMotion;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let containerStyle;
  if ($[9] !== height || $[10] !== width) {
    containerStyle = {
      position: "relative"
    };
    if (width !== undefined) {
      containerStyle.width = width;
    }
    if (height !== undefined) {
      containerStyle.height = height;
    }
    $[9] = height;
    $[10] = width;
    $[11] = containerStyle;
  } else {
    containerStyle = $[11];
  }
  if (shouldReduceMotion || logos.length <= 1) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        scale: 1.06,
        filter: "brightness(1.08)"
      };
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const t8 = width ? `${width}px` : "100vw";
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        objectFit: "contain"
      };
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    const t10 = `pointer-events-none select-none ${imageClassName}`;
    let t11;
    if ($[14] !== logos[0].alt || $[15] !== logos[0].src || $[16] !== t10 || $[17] !== t8) {
      t11 = <Image src={logos[0].src} alt={logos[0].alt} fill={true} priority={true} sizes={t8} style={t9} className={t10} draggable={false} />;
      $[14] = logos[0].alt;
      $[15] = logos[0].src;
      $[16] = t10;
      $[17] = t8;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    let t12;
    if ($[19] !== className || $[20] !== containerStyle || $[21] !== t11) {
      t12 = <motion.div ref={containerRef} className={className} style={containerStyle} whileHover={t7} transition={hoverTransition}>{t11}</motion.div>;
      $[19] = className;
      $[20] = containerStyle;
      $[21] = t11;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    return t12;
  }
  let t7;
  if ($[23] !== containerStyle) {
    t7 = {
      ...containerStyle,
      contain: "layout"
    };
    $[23] = containerStyle;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== currentIndex || $[26] !== imageClassName || $[27] !== logos || $[28] !== width) {
    let t9;
    if ($[30] !== currentIndex || $[31] !== imageClassName || $[32] !== width) {
      t9 = (logo, index) => {
        const isActive = index === currentIndex;
        return <motion.div key={logo.src} initial={false} animate={{
          opacity: isActive ? 1 : 0,
          scale: isActive ? 1 : 0.92
        }} variants={{
          idle: {
            filter: "brightness(1)"
          },
          hover: isActive ? {
            scale: 1.06,
            filter: "brightness(1.08)",
            transition: hoverTransition
          } : {}
        }} transition={morphTransition} style={{
          position: "absolute",
          inset: 0,
          pointerEvents: isActive ? "auto" : "none"
        }}><Image src={logo.src} alt={logo.alt} fill={true} priority={true} sizes={width ? `${width}px` : "100vw"} style={{
            objectFit: "contain"
          }} className={`pointer-events-none select-none ${imageClassName}`} draggable={false} /></motion.div>;
      };
      $[30] = currentIndex;
      $[31] = imageClassName;
      $[32] = width;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    t8 = logos.map(t9);
    $[25] = currentIndex;
    $[26] = imageClassName;
    $[27] = logos;
    $[28] = width;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[34] !== className || $[35] !== t7 || $[36] !== t8) {
    t9 = <motion.div ref={containerRef} className={className} style={t7} whileHover="hover" initial="idle" animate="idle">{t8}</motion.div>;
    $[34] = className;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
}

// React.memo with custom comparison for stable logos array
const MorphingLogo = memo(MorphingLogoInner, (prev, next) => {
  if (prev.logos.length !== next.logos.length) return false;
  for (let i = 0; i < prev.logos.length; i++) {
    if (prev.logos[i].src !== next.logos[i].src) return false;
  }
  return prev.width === next.width && prev.height === next.height && prev.initialDelay === next.initialDelay && prev.morphInterval === next.morphInterval && prev.className === next.className && prev.imageClassName === next.imageClassName;
});
MorphingLogo.displayName = "MorphingLogo";
export default MorphingLogo;